import { ACCOUNT_FEATURE, selectUserRolePermissions } from '@socialive/common';
import { useFeature } from '@socialive/features';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ModalTypes } from '../helpers/enums';
import trackMixPanelEvent from '../helpers/trackMixPanelEvent';
import { selectAvailableInstances, uploadVideoToTray } from '../store/connectionManager';
import CustomModal from './CustomModal';
import ListButton from './ListButton';

export default function ShareButton({ videoId, videoType, duration }) {
  const dispatch = useDispatch();
  const instancesToShare = useSelector(selectAvailableInstances);
  const { canPublishVideo } = useSelector(selectUserRolePermissions);
  const [, { enabled: rolesEnabled }] = useFeature(ACCOUNT_FEATURE.USER_ROLES);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const shareVideo = useCallback(
    async (instance) => {
      const {
        triggerUrl,
        title,
        network: { vendor },
      } = instance;

      const { error } = await dispatch(
        uploadVideoToTray({
          videoId,
          videoType,
          triggerUrl,
        })
      );

      trackMixPanelEvent('send video', {
        Duration: duration,
        Vendor: vendor,
        'Connection Title': title,
        'Source Video': Number(videoId),
        'Video Type': videoType,
      });

      if (error) {
        toast.error('Something went wrong. Please try again.');
      } else {
        setIsConfirmationModalOpen(true);
      }
    },
    [dispatch, videoId, videoType, duration]
  );

  const closeConfirmationModal = useCallback(() => setIsConfirmationModalOpen(false), []);

  const options = useMemo(
    () => [instancesToShare.map((instance) => ({ label: instance.title, onClick: () => shareVideo(instance) }))],
    [instancesToShare, shareVideo]
  );

  const label = 'Share Video';
  const tooltip = !rolesEnabled || canPublishVideo ? label : 'No permission to share';

  if (!instancesToShare.length) {
    return null;
  }

  return (
    <>
      <ListButton
        id={`share-${videoId}`}
        type="share"
        options={options}
        label={label}
        tooltip={tooltip}
        isDisabled={tooltip !== label}
      />
      {isConfirmationModalOpen && (
        <CustomModal
          isOpen
          includeButtons
          handleOnClose={closeConfirmationModal}
          handleOnConfirm={closeConfirmationModal}
          confirmButtonText="OK"
          confirmationText={
            <div>
              It may take some time for the transfer to complete.
              <br />
              You&#39;ll receive an email as soon as it is available.
            </div>
          }
          modalTitle="Transfer in Progress"
          type={ModalTypes.CONFIRMATION}
        />
      )}
    </>
  );
}

ShareButton.propTypes = {
  videoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  videoType: PropTypes.oneOf(['STREAM', 'CLIP']).isRequired,
  duration: PropTypes.number.isRequired,
};
